import React, { ChangeEvent, useEffect, useState } from 'react';
import Button from '../../../library/Button/button';
import Search from '../../Search';
import ConditionalLoader from '../../ConditionalLoader';
import IUserList from '../../../utils/UserList/UserList.interface';
import { GitOperations } from '../../../utils/Helper/enums';
import {
  IGitLog,
  IGitTrailResponse,
  IGitTrailResponseQueryFilters
} from '../../../utils/GitTrail/gitTrail.interface';
import GitTrail from '../../../utils/GitTrail/GitTrail';
import { addDays, getDateToday, substractDays } from '../../../utils/Date/transformDate';
import FilterBar from './FilterBar';
import Pagination from '../../../library/Pagination/Pagination';
import SortButton from '../../../library/Button/SortButton';
import LogsStatsBar from './LogsStatsBar';
import Select from '../../../library/Select/Select';
import { TGitLogKeys, TSortOrderObject } from '../../../utils/Helper/type';

interface Props {
  isLoading: boolean;
  error: string | null;
  users: Array<IUserList> | null;
  organizations: Array<string> | null;
}

const GitTrails = ({ isLoading, error, users, organizations }: Props) => {
  const operations = [GitOperations.GITPULL, GitOperations.GITPUSH];
  const searchFields: {
    key: TGitLogKeys;
    value: string;
  }[] = [
    {
      key: 'email',
      value: 'email'
    },
    {
      key: 'repositoryName',
      value: 'Repository'
    },
    {
      key: 'orgName',
      value: 'Organization'
    }
  ];

  const getDefaultFilter = () => {
    const today = getDateToday();
    return {
      start: Date.parse(substractDays(today, 2)),
      end: Date.parse(addDays(today, 1))
    };
  };

  const [gitTrails, setGitTrails] = useState<IGitTrailResponse | null>(null);
  const [query, setQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [fetchError, setFetchError] = useState<string>('');
  const [searchField, setSearchField] = useState<TGitLogKeys>('email');
  const [isFetchingLogs, setIsFetchingLogs] = useState<boolean>(true);
  const [isFetchingMoreLogs, setIsFetchingMoreLogs] = useState<boolean>(true);
  const [timePreference, setTimePreference] = useState<boolean>(false);
  const [filter, setFilter] = useState<IGitTrailResponseQueryFilters | null>(getDefaultFilter());
  const [isFailed, setIsFailed] = useState<boolean | null>(null);
  const [sortOrder, setSortOrder] = useState<TSortOrderObject>({
    current: 'time'
  });

  const screenHeight = window.innerHeight;
  const itemsPerPage = screenHeight ? Math.ceil((screenHeight - 310) / 90) : 10;

  const sortLogs = () => {
    return [...(gitTrails?.logs ?? [])].sort((log1: IGitLog, log2: IGitLog) => {
      const key: keyof IGitLog = sortOrder.current;
      const direction = sortOrder[key];
      const aVal = log1[key] ?? '';
      const bVal = log2[key] ?? '';
      return direction === 'asc' ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
    });
  };
  const sortedData = sortLogs();

  const filteredLogs = sortedData.filter((log) => {
    const logData = log[searchField] ?? '';
    const condition =
      isFailed !== null
        ? isFailed
          ? log.errorCode !== undefined
          : log.errorCode === undefined
        : true;
    return logData.toLowerCase().includes(query.toLowerCase()) && condition;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const totalPages = Math.max(Math.floor(filteredLogs.length / itemsPerPage), 1);
  const logsForThisPage = filteredLogs.slice(indexOfFirstItem, indexOfLastItem);

  const getGenricFieldName = () => {
    return searchFields.find((item) => item.key === searchField)?.value ?? searchFields[0].value;
  };

  const getGitTrails = async (filters: IGitTrailResponseQueryFilters, next?: string) => {
    try {
      setFetchError('');
      if (next) setIsFetchingMoreLogs(true);
      else setIsFetchingLogs(true);
      const gitTrail = new GitTrail();
      const query = gitTrail.genrateQueryFromFilter(filters, next);
      const data = await gitTrail.getGitLogs(query);

      if (data === null) {
        setFetchError('Oops! Something went wrong. Please refresh the page and try again.');
      } else {
        if (next && gitTrails) {
          setGitTrails({
            ...data,
            pullOperations: data.pullOperations + gitTrails.pullOperations,
            pushOperations: data.pushOperations + gitTrails.pushOperations,
            total: data.total + gitTrails.total,
            logs: [...gitTrails.logs, ...data.logs]
          });
        } else {
          setGitTrails(data);
        }
      }
    } catch (err) {
      console.error(err);
      setFetchError('Oops! Something went wrong. Please refresh the page and try again.');
    } finally {
      setIsFetchingLogs(false);
      setIsFetchingMoreLogs(false);
    }
  };

  const handleSubmitFilter = (filters: IGitTrailResponseQueryFilters) => {
    setQuery('');
    setFilter(filters);
    void getGitTrails(filters);
  };

  const handleGetMoreLogs = () => {
    if (filter && gitTrails) void getGitTrails(filter, gitTrails.key);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchFieldChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as TGitLogKeys;
    if (value) {
      setSearchField(value);
    }
  };

  const resetPageAndSearch = (newQuery: string) => {
    setQuery(newQuery);
    handlePageChange(1);
  };

  const getSortOrder = (key: string): boolean | null => {
    return sortOrder[key] === 'dec' && sortOrder.current === key
      ? true
      : sortOrder[key] === 'asc' && sortOrder.current === key
      ? false
      : null;
  };

  const handleSort = (key: TGitLogKeys) => {
    setSortOrder((prev: TSortOrderObject) => {
      const orderObj: TSortOrderObject = { ...prev };
      orderObj[key] = orderObj[key] === 'asc' ? 'dec' : orderObj[key] === 'dec' ? 'asc' : 'asc';
      orderObj.current = key;
      return orderObj;
    });
  };

  const handleTogleTimePreference = () => {
    setTimePreference((prvs) => !prvs);
  };

  const handleFilterBySuccessStatus = (isFailedStatus: boolean | null) => {
    setIsFailed(isFailedStatus);
  };

  useEffect(() => {
    void getGitTrails(getDefaultFilter());
  }, []);

  console.log(totalPages === currentPage && gitTrails && gitTrails.key);
  console.log(totalPages, currentPage, gitTrails, gitTrails?.key);

  return (
    <ConditionalLoader
      isLoading={isLoading}
      error={error}
      className="flex justify-center my-[4rem]"
    >
      <div>
        <FilterBar
          users={users}
          organizations={organizations}
          operations={operations}
          onSubmit={handleSubmitFilter}
        />
      </div>
      <div></div>
      <ConditionalLoader
        isLoading={isFetchingLogs}
        error={fetchError}
        className="flex justify-center my-[4rem]"
      >
        {gitTrails && sortedData.length ? (
          <div className="w-full h-full md:w-full">
            <LogsStatsBar trailStats={gitTrails} />
            <div className="bg-white block md:w-[100%] mx-auto md:ps-0.5 md:pe-0.5">
              <div className="flex flex-wrap gap-2 justify-end pt-2 pb-1">
                <div className="flex flex-grow justify-start gap-4">
                  <div
                    className={`flex justify-center items-center gap-2 p-2 ${
                      isFailed === null ? 'border-b-2 border-light-gray' : ''
                    }`}
                  >
                    <button
                      id="filter-status-all"
                      className="flex items-center gap-2"
                      onClick={() => {
                        handleFilterBySuccessStatus(null);
                      }}
                    >
                      <div className="w-1 h-1 rounded-full p-1 bg-light-gray"></div>
                      <span>All</span>
                    </button>
                  </div>
                  <div
                    className={`flex justify-center items-center gap-2 py-1 px-3 ${
                      isFailed ? 'border-b-2 border-red' : ''
                    }`}
                  >
                    <button
                      id="filter-status-failed"
                      className="flex items-center gap-2"
                      onClick={() => {
                        handleFilterBySuccessStatus(true);
                      }}
                    >
                      <div className="w-1 h-1 rounded-full p-1 bg-red"></div>
                      <span>Failed</span>
                    </button>
                  </div>
                  <div
                    className={`flex justify-center items-center gap-2 p-2 ${
                      isFailed === false ? 'border-b-2 border-green' : ''
                    }`}
                  >
                    <button
                      id="filter-status-success"
                      className="flex items-center gap-2"
                      onClick={() => {
                        handleFilterBySuccessStatus(false);
                      }}
                    >
                      <div className="w-1 h-1 rounded-full p-1 bg-green"></div>
                      <span>Success</span>
                    </button>
                  </div>
                </div>
                <div>
                  <Select
                    id="filter-search-field"
                    value={searchField}
                    onChange={handleSearchFieldChange}
                    options={searchFields}
                    error={null}
                    label="Search in Field"
                  />
                </div>
                <div className="flex-grow-0 px-1">
                  <Search
                    onChange={(e: any) => resetPageAndSearch(e.target.value)}
                    text={`Search by ${getGenricFieldName()}...`}
                    hideLabel
                  />
                </div>
              </div>
              <div className="w-full max-h-screen overflow-y-scroll">
                {filteredLogs.length ? (
                  <div>
                    <>
                      <div className="min-w-fit border rounded-md overflow-hidden">
                        <table className="w-full whitespace-nowrap rounded-lg bg-white divide-y divide-gray-300">
                          <thead className="bg-[#f8f8f8]">
                            <tr className="text-black text-center">
                              <th className="font-semibold text-sm uppercase px-6 py-4 text-left">
                                <div className="flex justify-between items-center">
                                  <div>
                                    <span>Time</span>
                                    <Button
                                      id="toggle-time-format"
                                      className="p-2 py-1 mx-1 border border-light-gray rounded-md"
                                      onclick={handleTogleTimePreference}
                                    >
                                      {timePreference ? 'UTC' : 'Locale'}
                                    </Button>
                                  </div>
                                  <SortButton
                                    id="time-sort"
                                    order={getSortOrder('time')}
                                    onClick={() => {
                                      handleSort('time');
                                    }}
                                  />
                                </div>
                              </th>
                              <th className="font-semibold text-sm uppercase px-6 py-4 text-left">
                                <div className="flex justify-between items-center">
                                  <span>Repository</span>
                                  <SortButton
                                    id="repository-sort"
                                    order={getSortOrder('repositoryName')}
                                    onClick={() => {
                                      handleSort('repositoryName');
                                    }}
                                  />
                                </div>
                              </th>
                              <th className="font-semibold text-sm uppercase px-6 py-4 text-left">
                                <div className="flex justify-between items-center">
                                  <span>User</span>
                                  <SortButton
                                    id="user-sort"
                                    order={getSortOrder('email')}
                                    onClick={() => {
                                      handleSort('email');
                                    }}
                                  />
                                </div>
                              </th>
                              <th className="font-semibold text-sm uppercase px-6 py-4 text-left">
                                <div className="flex justify-between items-center">
                                  <span>Organization</span>
                                  <SortButton
                                    id="org-sort"
                                    order={getSortOrder('orgName')}
                                    onClick={() => {
                                      handleSort('orgName');
                                    }}
                                  />
                                </div>
                              </th>
                              <th className="font-semibold text-sm uppercase px-6 py-4 text-left">
                                <div className="flex justify-between items-center">
                                  <span>Operation</span>
                                  <SortButton
                                    id="operation-sort"
                                    order={getSortOrder('eventName')}
                                    onClick={() => {
                                      handleSort('eventName');
                                    }}
                                  />
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {logsForThisPage.map((log) => (
                              <tr key={log.id}>
                                <td className="w-80 pl-6 py-4 text-left">
                                  {timePreference
                                    ? new Date(log.time).toUTCString()
                                    : new Date(log.time).toLocaleString()}
                                </td>
                                <td className="px-6 py-4 text-left">{log.repositoryName}</td>
                                <td className="px-6 py-4 text-left">{log.email}</td>
                                <td className="px-6 py-4 text-left">{log.orgName}</td>
                                <td className="flex items-center gap-2 px-6 py-4 text-left">
                                  {log.eventName === GitOperations.GITPULL ? (
                                    <>
                                      <span>Pull </span>
                                      {log.errorCode ? (
                                        <span
                                          className="bg-red text-white py-1 px-2 rounded-full text-xs cursor-default border border-red hover:border-light-gray"
                                          title={log.errorCode}
                                        >
                                          Failed
                                        </span>
                                      ) : (
                                        <></>
                                      )}
                                    </>
                                  ) : log.eventName === GitOperations.GITPUSH ? (
                                    <>
                                      <span>Push </span>
                                      {log.errorCode ? (
                                        <span
                                          className="bg-red text-white py-1 px-2 rounded-full text-xs cursor-default border border-red hover:border-light-gray"
                                          title={log.errorCode}
                                        >
                                          Failed
                                        </span>
                                      ) : (
                                        <></>
                                      )}
                                    </>
                                  ) : (
                                    log.eventName
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {totalPages === currentPage && gitTrails && gitTrails.key ? (
                        <div className="w-full flex justify-center py-2">
                          <Button
                            onclick={handleGetMoreLogs}
                            loadingColour="black"
                            loading={isFetchingMoreLogs}
                            buttonText="Load More"
                          />
                        </div>
                      ) : (
                        <></>
                      )}
                      {filteredLogs.length > itemsPerPage && (
                        <Pagination
                          className={'pagination-bar mt-4 flex justify-center items-center m-auto'}
                          currentPage={currentPage}
                          totalCount={filteredLogs.length}
                          pageSize={itemsPerPage}
                          onPageChange={handlePageChange}
                        />
                      )}
                    </>
                  </div>
                ) : (
                  <>
                    {query ? (
                      <p className="text-center font-bold pt-4">
                        Logs not found with{' '}
                        <b>
                          {getGenricFieldName()}: {query}
                        </b>
                      </p>
                    ) : (
                      <p className="text-center font-bold pt-4">Logs not found !!</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center font-bold pt-4">Logs not found !!</p>
        )}
      </ConditionalLoader>
    </ConditionalLoader>
  );
};

export default GitTrails;
